import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  FiArrowRight,
  FiCheck,
  FiSearch,
  FiFileText,
  FiLink2,
  FiDownload,
  FiDatabase,
} from "react-icons/fi";
import "./features.css";

const Features = () => {
  // Intersection Observer scroll trigger
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1 },
    );

    const animElements = document.querySelectorAll(".animate-on-scroll");
    animElements.forEach((el) => observer.observe(el));

    return () => {
      animElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // State for simulated extraction animation
  const [extracting, setExtracting] = useState(false);
  useEffect(() => {
    const interval = setInterval(() => {
      setExtracting(true);
      setTimeout(() => setExtracting(false), 2500);
    }, 4500);
    return () => clearInterval(interval);
  }, []);

  // State for search term context preview
  const [searchTerm, setSearchTerm] = useState("liability");
  const searchResults = [
    {
      page: 12,
      context:
        "...under standard contract guidelines, the liability clause covers...",
    },
    {
      page: 24,
      context:
        "...in no event shall the total liability of either party exceed...",
    },
    {
      page: 88,
      context:
        "...exemptions from this strict liability are outlined in Section 4...",
    },
  ];
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant", // or "smooth"
    });
  }, [pathname]);
  
  return (
    <div className="features-page-wrapper">
      {/* ==================== 1. HERO SECTION ==================== */}
      <section className="features-hero">
        <div className="container hero-grid">
          <div className="hero-text-content animate-on-scroll fade-up">
            <span className="badge-pill">FEATURES LIST</span>
            <h1>
              Powerful Tools for{" "}
              <span className="highlight-text">Deeper Reading</span>
            </h1>
            <p className="hero-subtitle">
              LiquidText combines document reading, annotation, research,
              note-taking, and visual connections into a single workspace.
            </p>
            <div className="hero-btn-group">
              <a href="#workspace" className="btn-primary">
                Explore Features <FiArrowRight className="btn-icon" />
              </a>
              <a href="/" className="btn-secondary">
                Back to Home
              </a>
            </div>
          </div>
          <div className="hero-visual-content animate-on-scroll fade-left">
            <div className="features-hero-mockup">
              <img
                src="/images/hero_workspace.png"
                alt="LiquidText Workspace Overview"
                className="features-hero-img img-fluid"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ==================== 2. DETAILED FEATURES SECTION ==================== */}

      {/* Feature 1: Unified Workspace */}
      <section id="workspace" className="feature-block-section bg-light">
        <div className="container grid-two-col">
          <div className="feature-block-visual animate-on-scroll fade-right">
            <div className="visual-card-wrapper shadow-lg">
              <img
                src="/images/liquidtext_active.png"
                alt="Unified Workspace screenshot"
                className="visual-card-img"
              />
            </div>
          </div>
          <div className="feature-block-text animate-on-scroll fade-left">
            <span className="feature-number">FEATURE 01</span>
            <h2>Unified Workspace</h2>
            <h3>Everything You Need. In One Place.</h3>
            <p className="feature-desc">
              Keep your source documents and working notes together. Create a
              workspace where documents, excerpts, notes, images, ideas, and
              connections can live side by side.
            </p>
            <div className="feature-key-takeaway">
              <strong>💡 Pro Tip:</strong> LiquidText's workspace model is one
              of its core differentiators, shifting you from passive reading to
              active brainstorming.
            </div>
          </div>
        </div>
      </section>

      {/* Feature 2: Extract Anything */}
      <section className="feature-block-section">
        <div className="container grid-two-col reverse-col">
          <div className="feature-block-text animate-on-scroll fade-right">
            <span className="feature-number">FEATURE 02</span>
            <h2>Extract Anything</h2>
            <h3>Turn Important Passages Into Working Notes</h3>
            <p className="feature-desc">
              Found something important? Pull text or excerpts directly from
              your documents and place them into your workspace. Instead of
              copying information into another application, keep your source and
              your thinking connected.
            </p>
            <ul className="check-list">
              <li>
                <FiCheck className="check-icon" /> Drag and drop text excerpts
                instantly
              </li>
              <li>
                <FiCheck className="check-icon" /> Capture charts, images, and
                tables
              </li>
              <li>
                <FiCheck className="check-icon" /> Keep a persistent link back
                to the source page
              </li>
            </ul>
          </div>

          <div className="feature-block-visual animate-on-scroll fade-left">
            <div className="extraction-simulator-card">
              <div className="sim-document">
                <div className="sim-header">📋 research_paper.pdf</div>
                <div className="sim-body">
                  <p>
                    The integration of green energy matrices has shown a{" "}
                    <strong>42% increase in storage efficiency</strong> when
                    paired with smart grid distributors...
                  </p>
                  <div
                    className={`sim-highlighter ${extracting ? "highlighting" : ""}`}
                  ></div>
                </div>
              </div>
              <div className="sim-workspace">
                <div className="sim-ws-header">Workspace Canvas</div>
                <div className="sim-ws-body">
                  <div
                    className={`sim-excerpt-node ${extracting ? "extracted" : ""}`}
                  >
                    💡 42% increase in storage efficiency
                    <div className="source-dot"></div>
                  </div>
                  <div className="sim-comment-node">
                    ✍️ Check feasibility for our 2026 project specs.
                  </div>
                </div>
              </div>
              {/* Connecting line simulation */}
              <div
                className={`sim-connector-line ${extracting ? "visible" : ""}`}
              ></div>
            </div>
            <div className="visual-caption text-center">
              Interactive CSS Extraction Simulator
            </div>
          </div>
        </div>
      </section>

      {/* Feature 3: Connect Ideas */}
      <section className="feature-block-section bg-light">
        <div className="container grid-two-col">
          <div className="feature-block-visual animate-on-scroll fade-right">
            <div className="visual-card-wrapper shadow-lg">
              <img
                src="/images/why_connect_ideas.png"
                alt="Connect Ideas screenshot"
                className="visual-card-img"
              />
            </div>
          </div>
          <div className="feature-block-text animate-on-scroll fade-left">
            <span className="feature-number">FEATURE 03</span>
            <h2>Connect Ideas</h2>
            <h3>See Relationships Others Miss</h3>
            <p className="feature-desc">
              Visual connections help you understand how individual pieces of
              information relate to one another. Draw simple connector lines
              between documents and annotations to map out structures.
            </p>
            <div className="connect-grid-badges">
              <span className="connect-badge">
                <FiLink2 /> Notes to notes
              </span>
              <span className="connect-badge">
                <FiLink2 /> Notes to documents
              </span>
              <span className="connect-badge">
                <FiLink2 /> Excerpts to excerpts
              </span>
              <span className="connect-badge">
                <FiLink2 /> Ideas to source material
              </span>
              <span className="connect-badge">
                <FiLink2 /> Across multiple files
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Feature 4: Search Without Losing Context */}
      <section className="feature-block-section">
        <div className="container grid-two-col reverse-col">
          <div className="feature-block-text animate-on-scroll fade-right">
            <span className="feature-number">FEATURE 04</span>
            <h2>Search Without Losing Context</h2>
            <h3>Find Information. Keep the Bigger Picture.</h3>
            <p className="feature-desc">
              Search for important information while maintaining the surrounding
              context. LiquidText's visual approach allows highlights and search
              results to be brought together so you can understand information
              without constantly losing your place.
            </p>
            <div className="search-pill-inputs">
              <div className="sim-search-bar">
                <FiSearch className="search-icon" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search keywords..."
                />
              </div>
            </div>
          </div>

          <div className="feature-block-visual animate-on-scroll fade-left">
            <div className="search-results-simulator">
              <div className="ws-preview-header">
                🔍 Search Highlights for "{searchTerm}"
              </div>
              <div className="search-hits-stack">
                {searchResults.map((hit, idx) => (
                  <div key={idx} className="search-hit-card">
                    <span className="hit-page">Pg {hit.page}</span>
                    <p className="hit-context">
                      {hit.context.replace(
                        new RegExp(searchTerm, "gi"),
                        (match) => `<strong>${match}</strong>`,
                      )}
                    </p>
                  </div>
                ))}
              </div>
              <div className="context-indicator-glow">
                Surrounding pages are collapsed to display results together.
              </div>
            </div>
            <div className="visual-caption text-center">
              Simulated Keyword Context Collapser
            </div>
          </div>
        </div>
      </section>

      {/* Feature 5: Compare Distant Information */}
      <section className="feature-block-section bg-light">
        <div className="container grid-two-col">
          <div className="feature-block-visual animate-on-scroll fade-right">
            <div className="visual-card-wrapper shadow-lg">
              <img
                src="/images/why_read_faster.png"
                alt="Compare Distant Information screenshot"
                className="visual-card-img"
              />
            </div>
          </div>
          <div className="feature-block-text animate-on-scroll fade-left">
            <span className="feature-number">FEATURE 05</span>
            <h2>Compare Distant Information</h2>
            <h3>Bring Separate Pages Together</h3>
            <p className="feature-desc">
              Important information isn't always located next to each other.
              LiquidText allows you to squeeze and fold distant sections of
              documents together to compare related information. This makes
              complex research easier to analyze.
            </p>
            <ul className="check-list">
              <li>
                <FiCheck className="check-icon" /> Squeeze documents together
                with multi-touch gestures
              </li>
              <li>
                <FiCheck className="check-icon" /> Hide pages in between to
                compare tables and formulas
              </li>
              <li>
                <FiCheck className="check-icon" /> Keep connectors intact even
                while the document is folded
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Feature 6: Write Anywhere */}
      <section className="feature-block-section">
        <div className="container grid-two-col reverse-col">
          <div className="feature-block-text animate-on-scroll fade-right">
            <span className="feature-number">FEATURE 06</span>
            <h2>Write Anywhere</h2>
            <h3>Think Naturally</h3>
            <p className="feature-desc">
              Use typed notes, handwritten annotations, ink, highlights, and
              visual connections to capture your thoughts the way that works
              best for you. Whether you prefer a stylus, mouse, or keyboard, the
              canvas adapts.
            </p>
            <div className="ink-indicators">
              <span className="ink-pill red-ink">✍️ Red Ink</span>
              <span className="ink-pill blue-ink">✍️ Blue Highlight</span>
              <span className="ink-pill yellow-ink">✍️ Yellow Highlight</span>
              <span className="ink-pill type-ink">⌨️ Typed Sticky</span>
            </div>
          </div>

          <div className="feature-block-visual animate-on-scroll fade-left">
            <div className="canvas-mockup-write">
              <div className="note-sticky-mockup ws-yellow">
                <span className="sticky-pin">📌</span>
                <h4>Project Hypothesis</h4>
                <p>
                  Verify if the 42% increase applies to smaller solar units too.
                </p>
              </div>
              <div className="handwriting-svg-container">
                {/* Simulated handwritten scribbles */}
                <svg
                  width="220"
                  height="100"
                  viewBox="0 0 220 100"
                  className="scribble-svg"
                >
                  <path
                    d="M10,80 Q30,20 60,70 T120,40 T180,60"
                    fill="none"
                    stroke="var(--accent)"
                    strokeWidth="3"
                    strokeLinecap="round"
                    className="scribble-path"
                  />
                </svg>
                <div className="svg-label">Ink Annotations Supported</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature 7: Import & Export */}
      <section className="feature-block-section bg-light">
        <div className="container grid-two-col">
          <div className="feature-block-visual animate-on-scroll fade-right">
            <div className="import-export-grid">
              <div className="format-card">
                <FiFileText className="format-icon" /> PDF Documents
              </div>
              <div className="format-card">📄 MS Word (DOCX)</div>
              <div className="format-card">📊 PowerPoint (PPTX)</div>
              <div className="format-card">🌐 Web Snippets</div>
              <div className="format-card">
                <FiDatabase className="format-icon" /> Dropbox Sync
              </div>
              <div className="format-card">💾 Google Drive / OneDrive</div>
            </div>
          </div>
          <div className="feature-block-text animate-on-scroll fade-left">
            <span className="feature-number">FEATURE 07</span>
            <h2>Import & Export</h2>
            <h3>Work With the Files You Already Use</h3>
            <p className="feature-desc">
              LiquidText supports popular document formats and integrations,
              including PDF, Word, PowerPoint, web content, and cloud storage
              services. Export your work so your research and conclusions can be
              shared with others easily.
            </p>
            <button
              className="btn-secondary"
              onClick={() => alert("Integrated with cloud services.")}
            >
              <FiDownload className="btn-icon" /> Explore Integrations
            </button>
          </div>
        </div>
      </section>

      {/* Feature 8: Collaboration */}
      <section className="feature-block-section">
        <div className="container grid-two-col reverse-col">
          <div className="feature-block-text animate-on-scroll fade-right">
            <span className="feature-number">FEATURE 08</span>
            <h2>Collaboration</h2>
            <h3>Research Together</h3>
            <p className="feature-desc">
              Share annotated documents and collaborate with colleagues.
              LiquidText's collaboration capabilities allow users to review and
              contribute to shared work, making group studies, legal reviews,
              and corporate projects simple.
            </p>
            <ul className="check-list">
              <li>
                <FiCheck className="check-icon" /> Shared workspaces with
                continuous backup
              </li>
              <li>
                <FiCheck className="check-icon" /> Real-time peer additions and
                edits
              </li>
              <li>
                <FiCheck className="check-icon" /> Retain individual highlight
                colours for clarity
              </li>
            </ul>
          </div>
          <div className="feature-block-visual animate-on-scroll fade-left">
            <div className="visual-card-wrapper shadow-lg">
              <img
                src="/images/work_across_devices.png"
                alt="Collaboration across devices"
                className="visual-card-img"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ==================== 3. CALL TO ACTION ==================== */}
      <section className="features-cta-section">
        <div className="cta-box container text-center animate-on-scroll fade-up">
          <h2>Ready to Unlock D0C/PDF Research?</h2>
          <p>
            LiquidText brings reading, writing, and connecting ideas together in
            one workspace.
          </p>
          <a href="#workspace" className="btn-cta-primary">
            Start Structuring Ideas
          </a>
        </div>
      </section>
    </div>
  );
};

export default Features;
